import {AGENCY_KEYS} from "@/api/agencyApi";
import type {TokenClaimsSerializer} from "@/generated/DjangoTypes";
import {client} from "@/httpClient";
import useTokenStore from "@/store/token";
import type {TAgency} from "@/store/types";
import {createFileRoute, redirect, type ParsedLocation} from "@tanstack/react-router";
import {jwtDecode} from "jwt-decode";

export const Route = createFileRoute("/_authenticated/_agency")({
  beforeLoad: async ({
    location,
    params,
  }: {
    location: ParsedLocation;
    params: {agencyId?: number; companyId?: number};
  }) => {
    const token = useTokenStore.getState().access;
    if (!token) {
      throw redirect({to: "/login", replace: true});
    }

    const claims = jwtDecode<TokenClaimsSerializer>(token);
    const companyId = claims.sub_brand_ids?.[0];
    const routeAgencyId = params.agencyId ? Number(params.agencyId) : undefined;

    const isAdmin = claims.access_level === "COMPANY_ADMIN" || claims.access_level === "INFLUENCER_ADMIN";
    const isAgency = claims.access_level === "AGENCY";

    const redirectToCompany = () => {
      if (!companyId) {
        throw redirect({
          to: "/",
          replace: true,
        });
      }
      throw redirect({
        to: "/company/$companyId/dashboard",
        params: {companyId},
        replace: true,
      });
    };

    if (!isAdmin && !isAgency) {
      const routeCompanyId = Number(params.companyId);

      if (routeAgencyId && routeCompanyId && claims.sub_brand_ids?.includes(routeCompanyId)) {
        const chatPath = `/agency/${routeAgencyId}/chat/${routeCompanyId}`;
        const href = location.pathname.startsWith(chatPath)
          ? location.href.replace(chatPath, `/company/${routeCompanyId}/chat`)
          : location.href.replace(`/agency/${routeAgencyId}/subbrand/${routeCompanyId}`, `/company/${routeCompanyId}`);

        throw redirect({href, replace: true});
      }

      redirectToCompany();
    }

    if (!routeAgencyId) {
      redirectToCompany();
    }

    if (!isAdmin && !claims.agency_ids?.includes(routeAgencyId!)) {
      throw redirect({to: "/agency/$agencyId/dashboard", params: {agencyId: claims.agency_ids?.[0]!}, replace: true});
    }

    if (isAdmin) {
      try {
        const {data: agency} = await client.get<TAgency>(AGENCY_KEYS.qAgency(routeAgencyId!)[0]);

        if (!agency.multiple_brands) {
          redirectToCompany();
        }
      } catch (error) {
        console.error("Could not fetch agency data:", error);
        redirectToCompany();
      }
    }
  },
});
